import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { Printer, PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#1e2a5a";

export default function PublicPieceworkerStatement() {
  const { token } = useParams();
  const [st, setSt] = useState(null);
  const [error, setError] = useState("");
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/pieceworker-statements/${token}`);
      setSt(data);
      setSignName(data.worker?.name || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Enlace no válido");
    }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitSignature = async (dataUrl) => {
    setSaving(true);
    try {
      await api.post(`/public/pieceworker-statements/${token}/sign`, { signature: dataUrl, name: signName });
      toast.success("¡Estado de cuenta firmado, gracias!");
      setSignOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la firma");
    }
    setSaving(false);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: "#0a0f1d" }}>
        <div>
          <div className="text-4xl mb-3">⚠️</div>
          <div className="text-slate-200 font-semibold">{error}</div>
          <div className="text-slate-500 text-sm mt-2">Solicita un nuevo enlace de firma a Corporation Inconti.</div>
        </div>
      </div>
    );
  }
  if (!st) return <div className="min-h-screen flex items-center justify-center text-slate-400" style={{ background: "#0a0f1d" }}>Cargando…</div>;

  const projName = (id) => {
    const p = (st.projects || []).find((x) => x.id === id);
    return p ? p.code : "—";
  };
  const contracts = st.contracts || [];
  const payments = st.payments || [];
  const totals = contracts.reduce((a, c) => {
    const pu = Number(c.unit_price) || 0;
    a.contracted += (Number(c.total_units) || 0) * pu;
    a.paid += (Number(c.paid_units) || 0) * pu;
    return a;
  }, { contracted: 0, paid: 0 });
  const balance = Math.max(0, totals.contracted - totals.paid);

  return (
    <div className="min-h-screen" style={{ background: "#0a0f1d" }} data-testid="public-pieceworker-statement-page">
      <div className="no-print sticky top-0 z-10 px-4 py-3 flex items-center justify-between gap-2"
        style={{ borderBottom: "1px solid rgba(148,163,184,0.2)", background: "rgba(5,8,18,0.9)", backdropFilter: "blur(8px)", paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
        <div className="text-xs text-slate-300 uppercase tracking-widest font-semibold">Estado de cuenta · Inconti</div>
        <button onClick={() => window.print()} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 text-slate-200" style={{ border: "1px solid rgba(148,163,184,0.35)" }}>
          <Printer className="w-4 h-4" /> Imprimir
        </button>
      </div>

      <div className="py-6 px-2 sm:px-4 flex justify-center">
        <div className="bg-white text-slate-900 w-full max-w-[820px] shadow-xl" style={{ fontFamily: "Helvetica, Arial, sans-serif" }}>
          <div className="px-5 py-3 flex items-center justify-between text-white" style={{ background: NAVY }}>
            <div className="font-bold text-sm tracking-wide">INCONTI // CORPORATION</div>
            <div className="text-[11px]">Emitido: {fmtDate(st.created_at)}</div>
          </div>
          <div className="px-5 py-4">
            <div className="text-lg font-bold" style={{ color: NAVY }}>ESTADO DE CUENTA · DESTAJISTA</div>
            <div className="text-[11px] text-slate-500 mb-3">Comprobante de trabajos por unidad de obra, pagos entregados y saldos.</div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] border border-slate-300 p-3 mb-4">
              <div><span className="font-bold text-slate-600">DESTAJISTA: </span>{st.worker?.name}</div>
              <div><span className="font-bold text-slate-600">ESPECIALIDAD: </span>{st.worker?.specialty || "—"}</div>
              <div><span className="font-bold text-slate-600">TELÉFONO: </span>{st.worker?.phone || "—"}</div>
              <div><span className="font-bold text-slate-600">RFC: </span>{st.worker?.rfc || "—"}</div>
              <div className="col-span-2"><span className="font-bold text-slate-600">ALCANCE: </span>{st.project ? `${st.project.code} · ${st.project.name}` : "Todos los proyectos"}</div>
            </div>

            <div className="text-[12px] font-bold mb-1" style={{ color: NAVY }}>CONTRATOS DE DESTAJO</div>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-[11px] border-collapse min-w-[560px]">
                <thead>
                  <tr className="text-white" style={{ background: NAVY }}>
                    {["PROYECTO", "TRABAJO", "P.U.", "CONTRATADO", "PAGADO", "SALDO"].map((h) => <th key={h} className="px-2 py-1.5 text-left border border-slate-300">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {contracts.length === 0 && <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-400 border border-slate-300">Sin contratos.</td></tr>}
                  {contracts.map((c) => {
                    const pu = Number(c.unit_price) || 0;
                    const paid = (Number(c.paid_units) || 0) * pu;
                    const bal = Math.max(0, (Number(c.completed_units) || 0) * pu - paid);
                    return (
                      <tr key={c.id}>
                        <td className="px-2 py-1.5 border border-slate-300">{projName(c.project_id)}</td>
                        <td className="px-2 py-1.5 border border-slate-300">{c.description}</td>
                        <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap">{fmtMXN(pu)}</td>
                        <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap">{c.completed_units}/{c.total_units} {c.unit}</td>
                        <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap">{fmtMXN(paid)}</td>
                        <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap">{fmtMXN(bal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-[12px] font-bold mb-1" style={{ color: NAVY }}>PAGOS ENTREGADOS</div>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-[11px] border-collapse min-w-[560px]">
                <thead>
                  <tr className="text-white" style={{ background: NAVY }}>
                    {["FECHA", "PROYECTO", "TRABAJO", "UNIDADES", "MONTO", "NOTAS"].map((h) => <th key={h} className="px-2 py-1.5 text-left border border-slate-300">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-400 border border-slate-300">Sin pagos.</td></tr>}
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td className="px-2 py-1.5 border border-slate-300 whitespace-nowrap">{fmtDate(p.payment_date || p.created_at)}</td>
                      <td className="px-2 py-1.5 border border-slate-300">{projName(p.project_id)}</td>
                      <td className="px-2 py-1.5 border border-slate-300">{p.contract_description || "—"}</td>
                      <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap">{p.units_paid} {p.unit || ""}</td>
                      <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap font-semibold">{fmtMXN(p.amount)}</td>
                      <td className="px-2 py-1.5 border border-slate-300">{p.notes || "—"}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-100 font-bold">
                    <td colSpan={4} className="px-2 py-1.5 border border-slate-300 text-right">TOTAL PAGADO</td>
                    <td className="px-2 py-1.5 border border-slate-300 text-right whitespace-nowrap">{fmtMXN(st.total_paid)}</td>
                    <td className="border border-slate-300" />
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-6">
              <table className="text-[12px] border-collapse">
                <tbody>
                  <tr><td className="px-3 py-1 border border-slate-300 bg-slate-100 font-bold">TOTAL CONTRATADO</td><td className="px-3 py-1 border border-slate-300 text-right font-bold">{fmtMXN(totals.contracted)}</td></tr>
                  <tr><td className="px-3 py-1 border border-slate-300 bg-slate-100 font-bold">TOTAL PAGADO</td><td className="px-3 py-1 border border-slate-300 text-right font-bold text-green-700">{fmtMXN(st.total_paid)}</td></tr>
                  <tr><td className="px-3 py-1 border border-slate-300 bg-slate-100 font-bold">SALDO POR PAGAR</td><td className="px-3 py-1 border border-slate-300 text-right font-bold text-amber-700">{fmtMXN(balance)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-4">
              <div className="text-center pt-10">
                <div className="border-t border-slate-400 pt-1 text-[10px] font-bold text-slate-600 mx-4">ELABORÓ · INCONTI</div>
              </div>
              <div className="text-center" data-testid="statement-sign-area">
                {st.signature ? (
                  <div>
                    <img src={st.signature} alt="Firma" className="mx-auto h-16 object-contain" />
                    <div className="border-t border-slate-400 pt-1 text-[10px] font-bold text-slate-600 mx-4">
                      FIRMADO DIGITALMENTE · {(st.signed_name || "").toUpperCase()} · {fmtDate(st.signed_at)}
                    </div>
                    <div className="no-print mt-1 text-[11px] text-green-700 font-semibold flex items-center justify-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Firmado de conformidad</div>
                  </div>
                ) : (
                  <div>
                    <div className="pt-6" />
                    <div className="border-t border-slate-400 pt-1 text-[10px] font-bold text-slate-600 mx-4">RECIBÍ DE CONFORMIDAD · {(st.worker?.name || "").toUpperCase()}</div>
                    <div className="no-print mt-3">
                      <input data-testid="statement-sign-name" value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Nombre de quien firma"
                        className="w-full max-w-[240px] mx-auto block border border-slate-300 rounded-sm px-2 py-1.5 text-[12px] mb-2" />
                      <button data-testid="statement-sign-btn" onClick={() => setSignOpen(true)}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm text-white inline-flex items-center gap-2" style={{ background: NAVY }}>
                        <PenLine className="w-4 h-4" /> Firmar de recibido
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center text-[9px] text-slate-400 pb-4">Documento generado por el sistema de gestión INCONTI · No es un comprobante fiscal (CFDI).</div>
          </div>
        </div>
      </div>

      {signOpen && (
        <SignaturePad title={`Firma de ${signName || st.worker?.name}`} saving={saving}
          onConfirm={submitSignature} onClose={() => setSignOpen(false)} />
      )}
    </div>
  );
}
